import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';

export interface AssetFieldConfig {
  visible: boolean;
  required: boolean;
  label?: string;
}

export interface AssetFieldSettings {
  tag_id: AssetFieldConfig;
  description: AssetFieldConfig;
  brand: AssetFieldConfig;
  model: AssetFieldConfig;
  serial_number: AssetFieldConfig;
  status: AssetFieldConfig;
  purchase_date: AssetFieldConfig;
  purchased_from: AssetFieldConfig;
  cost: AssetFieldConfig;
  warranty_period: AssetFieldConfig;
  category_id: AssetFieldConfig;
  location_id: AssetFieldConfig;
  department_id: AssetFieldConfig;
  photo_url: AssetFieldConfig;
}

export interface OrganizationSettings {
  assetFields?: AssetFieldSettings;
}

// Default configuration for asset fields
// Only Description and Tag ID are required by default (like Asset Tiger)
// Users can enable required validation for other fields in Settings
const defaultAssetFieldSettings: AssetFieldSettings = {
  tag_id: { visible: true, required: true, label: 'Tag ID' },
  description: { visible: true, required: true, label: 'Description' },
  brand: { visible: true, required: false, label: 'Brand' },
  model: { visible: true, required: false, label: 'Model' },
  serial_number: { visible: true, required: false, label: 'Serial Number' },
  status: { visible: true, required: false, label: 'Status' },
  purchase_date: { visible: true, required: false, label: 'Purchase Date' },
  purchased_from: { visible: true, required: false, label: 'Purchased From' },
  cost: { visible: true, required: false, label: 'Cost' },
  warranty_period: { visible: true, required: false, label: 'Warranty Period (months)' },
  category_id: { visible: true, required: false, label: 'Category' },
  location_id: { visible: true, required: false, label: 'Location' },
  department_id: { visible: true, required: false, label: 'Department' },
  photo_url: { visible: true, required: false, label: 'Photo' },
};

async function fetchOrganizationSettings(organizationId: string): Promise<OrganizationSettings> {
  const { data, error } = await supabase
    .from('organizations')
    .select('settings')
    .eq('id', organizationId)
    .single();

  if (error) throw error;

  return (data?.settings as OrganizationSettings) || {};
}

async function updateOrganizationSettings(
  organizationId: string,
  settings: OrganizationSettings
): Promise<void> {
  const { error } = await supabase
    .from('organizations')
    .update({ settings })
    .eq('id', organizationId);

  if (error) throw error;
}

export function useOrganizationSettings(organizationId: string) {
  return useQuery({
    queryKey: ['organization-settings', organizationId],
    queryFn: () => fetchOrganizationSettings(organizationId),
    enabled: !!organizationId,
  });
}

export function useAssetFieldSettings(organizationId: string) {
  const { data: settings } = useOrganizationSettings(organizationId);

  return {
    ...defaultAssetFieldSettings,
    ...(settings?.assetFields || {}),
  };
}

export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, settings }: { organizationId: string; settings: OrganizationSettings }) =>
      updateOrganizationSettings(organizationId, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organization-settings', variables.organizationId] });
      notifications.show({
        title: 'Settings updated',
        message: 'Organization settings have been updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update settings. Please try again.',
        color: 'red',
      });
    },
  });
}
